/**
 * ModuleAutomationsPanel — reusable per-module automation controls.
 *
 * Drop into any module page with `<ModuleAutomationsPanel module="hr" />`
 * and it shows:
 *   • The active automations scoped to that module (toggle / run / edit)
 *   • Suggested templates for that module (one-click install)
 *   • A "Build in this module" button that opens the visual builder pre-scoped
 *
 * Keeps automation configuration IN CONTEXT with the module — the user doesn't
 * have to leave HR to set up an HR automation.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Zap, Sparkles, Play, Pencil, Plus, ChevronRight } from 'lucide-react';
import AutomationBuilderModal from './AutomationBuilderModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/automation-engine`;
const authHeaders = () => {
  const t = localStorage.getItem('konnect_access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const MODULE_ACCENT = {
  hr:        { color: 'bg-purple-100 text-purple-800 border-purple-200', label: 'HR' },
  sales:     { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Sales' },
  crm:       { color: 'bg-teal-100 text-teal-800 border-teal-200', label: 'CRM' },
  finance:   { color: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Finance' },
  marketing: { color: 'bg-pink-100 text-pink-800 border-pink-200', label: 'Marketing' },
  service:   { color: 'bg-blue-100 text-blue-800 border-blue-200', label: 'Service' },
  operations:{ color: 'bg-slate-100 text-slate-800 border-slate-200', label: 'Operations' },
  analytics: { color: 'bg-indigo-100 text-indigo-800 border-indigo-200', label: 'Analytics' },
};

export default function ModuleAutomationsPanel({ module, title }) {
  const [automations, setAutomations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const accent = MODULE_ACCENT[module] || { color: 'bg-gray-100 text-gray-800 border-gray-200', label: module };

  const load = async () => {
    setLoading(true);
    // Promise.allSettled — same rationale as AutomationCenterPage:
    // one broken sibling can't zero out the templates for the whole panel.
    const [a, t] = await Promise.allSettled([
      axios.get(API, { headers: authHeaders() }),
      axios.get(`${API}/templates`, { headers: authHeaders() }),
    ]);
    if (a.status === 'fulfilled') setAutomations((a.value.data.automations || []).filter(x => x.module === module));
    if (t.status === 'fulfilled') setTemplates((t.value.data.templates || []).filter(x => x.module === module));
    setLoading(false);
  };

  useEffect(() => { load(); }, [module]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeCount = automations.filter(a => a.active).length;
  const installedTemplateIds = useMemo(
    () => new Set(automations.map(a => a.installed_from_template).filter(Boolean)),
    [automations]
  );

  const toggle = async (a) => {
    try {
      await axios.patch(`${API}/${a.id}`, { active: !a.active }, { headers: authHeaders() });
      toast.success(a.active ? 'Automation paused' : 'Automation active');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Toggle failed'); }
  };

  const runNow = async (id) => {
    try {
      const r = await axios.post(`${API}/${id}/execute`, { manual: true }, { headers: authHeaders() });
      toast.success(`Ran ${r.data.action_count} actions`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Run failed'); }
  };

  const install = async (tplId) => {
    try {
      await axios.post(`${API}/templates/${tplId}/install`, {}, { headers: authHeaders() });
      toast.success('Automation installed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Install failed'); }
  };

  return (
    <Card data-testid={`module-automations-${module}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4 text-emerald-600" />
            {title || 'Automations'}
            <Badge className={`${accent.color} border text-[10px] font-medium`}>{accent.label}</Badge>
            {activeCount > 0 && (
              <span className="text-xs text-emerald-700 font-medium">{activeCount} active</span>
            )}
          </CardTitle>
          <Button
            size="sm" variant="outline"
            onClick={() => { setEditing({ module }); setBuilderOpen(true); }}
            data-testid={`module-build-${module}`}
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Build
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Active automations for this module */}
        {automations.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-medium mb-2 font-semibold">Your Automations</div>
            <div className="space-y-2">
              {automations.map(a => (
                <div key={a.id} className="flex items-center justify-between border border-gray-border rounded-lg p-2.5 hover:bg-gray-50 transition-colors" data-testid={`module-auto-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">{a.name}</div>
                    <div className="text-[11px] text-gray-medium">
                      {a.trigger?.type} · {a.actions?.length || 0} actions · {a.run_count || 0} runs
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Switch checked={!!a.active} onCheckedChange={() => toggle(a)} data-testid={`module-toggle-${a.id}`} />
                    <Button size="icon" variant="ghost" onClick={() => runNow(a.id)} disabled={!a.active}>
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(a); setBuilderOpen(true); }}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggested templates */}
        {templates.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-wide text-gray-medium mb-2 font-semibold">Suggested for {accent.label}</div>
            <div className="space-y-2">
              {templates.slice(0, 4).map(t => {
                const installed = installedTemplateIds.has(t.id);
                return (
                  <div key={t.id} className="flex items-start justify-between gap-2 border border-dashed border-gray-border rounded-lg p-2.5" data-testid={`module-tpl-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-primary flex items-center gap-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">{t.name}</span>
                      </div>
                      <div className="text-[11px] text-gray-medium mt-0.5 line-clamp-1">{t.description}</div>
                    </div>
                    <Button
                      size="sm"
                      variant={installed ? 'outline' : 'default'}
                      disabled={installed}
                      onClick={() => install(t.id)}
                      className={`flex-shrink-0 h-7 text-xs ${installed ? '' : 'bg-emerald-brand hover:bg-emerald-hover'}`}
                      data-testid={`module-install-${t.id}`}
                    >
                      {installed ? 'Installed' : 'Install'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!loading && automations.length === 0 && templates.length === 0 && (
          <div className="text-xs text-gray-medium text-center py-3">
            No automations in this module yet.
            <a href="/automation-center" className="text-emerald-700 hover:underline ml-1 inline-flex items-center gap-0.5">
              Browse the full library <ChevronRight className="h-3 w-3" />
            </a>
          </div>
        )}
      </CardContent>

      <AutomationBuilderModal
        open={builderOpen}
        onClose={() => { setBuilderOpen(false); setEditing(null); }}
        onSaved={load}
        initial={editing}
      />
    </Card>
  );
}
